"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return BaseUser;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _common = require("@nestjs/common");
const _Base = /*#__PURE__*/ _interop_require_default(require("~/models/Base"));
const _globals = require("~/utils/globals");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _NocoCache = /*#__PURE__*/ _interop_require_default(require("~/cache/NocoCache"));
const _extractProps = require("~/helpers/extractProps");
const _modelUtils = require("~/utils/modelUtils");
const _catchError = require("~/helpers/catchError");
const _commandPaletteHelpers = require("~/helpers/commandPaletteHelpers");
const _index = require("~/models/index");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
const logger = new _common.Logger('BaseUser');
let BaseUser = class BaseUser {
    static castType(baseUser) {
        return baseUser && new BaseUser(baseUser);
    }
    static async bulkInsert(context, baseUsers, ncMeta = _Noco.default.ncMeta) {
        const insertObj = baseUsers.map((baseUser)=>(0, _extractProps.extractProps)(baseUser, [
                'fk_user_id',
                'base_id',
                'roles',
                'invited_by'
            ]));
        if (!insertObj.length) {
            return;
        }
        const bulkData = await ncMeta.bulkMetaInsert(context.workspace_id, context.base_id, _globals.MetaTable.PROJECT_USERS, insertObj, true);
        const uniqueFks = [
            ...new Set(bulkData.map((d)=>d.base_id))
        ];
        for (const fk of uniqueFks){
            await _NocoCache.default.deepDel(context, `${_globals.CacheScope.BASE_USER}:${fk}:list`, _globals.CacheDelDirection.PARENT_TO_CHILD);
        }
        for (const d of bulkData){
            await _NocoCache.default.set(context, `${_globals.CacheScope.BASE_USER}:${d.base_id}:${d.fk_user_id}`, d);
            await _NocoCache.default.appendToList(context, _globals.CacheScope.BASE_USER, [
                d.base_id
            ], `${_globals.CacheScope.BASE_USER}:${d.base_id}:${d.fk_user_id}`);
            (0, _commandPaletteHelpers.cleanCommandPaletteCacheForUser)(d.fk_user_id).catch(()=>{
                logger.error('Error cleaning command palette cache');
            });
        }
    }
    static async insert(context, baseUser, ncMeta = _Noco.default.ncMeta) {
        const insertObj = (0, _extractProps.extractProps)(baseUser, [
            'fk_user_id',
            'base_id',
            'roles',
            'invited_by',
            'starred',
            'order',
            'hidden'
        ]);
        const { base_id, fk_user_id } = await ncMeta.metaInsert2(context.workspace_id, context.base_id, _globals.MetaTable.PROJECT_USERS, insertObj, true);
        // delete list to fetch updated list next time
        await _NocoCache.default.deepDel(context, `${_globals.CacheScope.BASE_USER}:${base_id}:list`, _globals.CacheDelDirection.PARENT_TO_CHILD);
        const res = await this.get(context, base_id, fk_user_id, ncMeta);
        (0, _commandPaletteHelpers.cleanCommandPaletteCacheForUser)(fk_user_id).catch(()=>{
            logger.error('Error cleaning command palette cache');
        });
        return res;
    }
    // public static async update(id, user: Partial<BaseUser>, ncMeta = Noco.ncMeta) {
    //   // return await ncMeta.metaUpdate(context.workspace_id, context.base_id, insertObj);
    // }
    static async get(context, baseId, userId, ncMeta = _Noco.default.ncMeta) {
        let baseUser = baseId && userId && await _NocoCache.default.get(context, `${_globals.CacheScope.BASE_USER}:${baseId}:${userId}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!baseUser || !baseUser.roles) {
            const queryBuilder = ncMeta.knex(_globals.MetaTable.USERS).select(`${_globals.MetaTable.USERS}.id`, `${_globals.MetaTable.USERS}.email`, `${_globals.MetaTable.USERS}.display_name`, `${_globals.MetaTable.USERS}.invite_token`, `${_globals.MetaTable.USERS}.roles as main_roles`, `${_globals.MetaTable.USERS}.created_at as created_at`, `${_globals.MetaTable.USERS}.meta`, `${_globals.MetaTable.PROJECT_USERS}.base_id`, `${_globals.MetaTable.PROJECT_USERS}.roles as roles`);
            queryBuilder.leftJoin(_globals.MetaTable.PROJECT_USERS, function() {
                this.on(`${_globals.MetaTable.PROJECT_USERS}.fk_user_id`, '=', `${_globals.MetaTable.USERS}.id`).andOn(`${_globals.MetaTable.PROJECT_USERS}.base_id`, '=', ncMeta.knex.raw('?', [
                    baseId
                ]));
            });
            queryBuilder.where(`${_globals.MetaTable.USERS}.id`, userId);
            baseUser = await queryBuilder.first();
            if (baseUser) {
                baseUser.meta = (0, _modelUtils.parseMetaProp)(baseUser);
                await _NocoCache.default.set(context, `${_globals.CacheScope.BASE_USER}:${baseId}:${userId}`, baseUser);
            }
        }
        // decide if user is mapped to base by checking if base_id is present
        // base_id will be null if base_user entry is not present
        if (baseUser) {
            baseUser.is_mapped = !!baseUser.base_id;
        }
        return this.castType(baseUser);
    }
    static async getUsersList(context, { base_id, mode = 'full', strict_in_record = false, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    include_ws_deleted = true, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    include_internal_user = false, user_ids }, ncMeta = _Noco.default.ncMeta) {
        const cachedList = await _NocoCache.default.getList(context, _globals.CacheScope.BASE_USER, [
            base_id
        ]);
        let { list: baseUsers } = cachedList;
        const { isNoneList } = cachedList;
        const fullVersionCols = [
            'invite_token'
        ];
        if (strict_in_record || !isNoneList && !baseUsers.length) {
            const queryBuilder = ncMeta.knex(_globals.MetaTable.USERS).select(`${_globals.MetaTable.USERS}.id`, `${_globals.MetaTable.USERS}.email`, `${_globals.MetaTable.USERS}.display_name`, `${_globals.MetaTable.USERS}.invite_token`, `${_globals.MetaTable.USERS}.roles as main_roles`, `${_globals.MetaTable.USERS}.created_at as created_at`, `${_globals.MetaTable.USERS}.meta`, `${_globals.MetaTable.PROJECT_USERS}.base_id`, `${_globals.MetaTable.PROJECT_USERS}.roles as roles`);
            const joinClause = strict_in_record ? 'innerJoin' : 'leftJoin';
            queryBuilder[joinClause](_globals.MetaTable.PROJECT_USERS, function() {
                this.on(`${_globals.MetaTable.PROJECT_USERS}.fk_user_id`, '=', `${_globals.MetaTable.USERS}.id`).andOn(`${_globals.MetaTable.PROJECT_USERS}.base_id`, '=', ncMeta.knex.raw('?', [
                    base_id
                ]));
            });
            baseUsers = await queryBuilder;
            baseUsers = baseUsers.map((baseUser)=>{
                if (baseUser) {
                    baseUser.base_id = base_id;
                    baseUser.meta = (0, _modelUtils.parseMetaProp)(baseUser);
                }
                return this.castType(baseUser);
            });
            if (!strict_in_record) {
                await _NocoCache.default.setList(context, _globals.CacheScope.BASE_USER, [
                    base_id
                ], baseUsers, [
                    'base_id',
                    'id'
                ]);
            }
        }
        if (user_ids) {
            baseUsers = baseUsers.filter((u)=>user_ids.includes(u.id));
        }
        if (mode === 'full') {
            return baseUsers;
        }
        // remove full version props if viewer
        for (const user of baseUsers){
            for (const prop of fullVersionCols){
                delete user[prop];
            }
        }
        return baseUsers;
    }
    static async getUsersCount(context, { base_id, query }, ncMeta = _Noco.default.ncMeta) {
        const queryBuilder = ncMeta.knex(_globals.MetaTable.USERS);
        if (query) {
            queryBuilder.where('email', 'like', `%${query.toLowerCase == null ? void 0 : query.toLowerCase.call(query)}%`);
        }
        queryBuilder.leftJoin(_globals.MetaTable.PROJECT_USERS, function() {
            this.on(`${_globals.MetaTable.PROJECT_USERS}.fk_user_id`, '=', `${_globals.MetaTable.USERS}.id`).andOn(`${_globals.MetaTable.PROJECT_USERS}.base_id`, '=', ncMeta.knex.raw('?', [
                base_id
            ]));
        });
        return (await queryBuilder.count('id', {
            as: 'count'
        }).first()).count;
    }
    static async updateRoles(context, baseId, userId, roles, ncMeta = _Noco.default.ncMeta) {
        // set meta
        const res = await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.PROJECT_USERS, {
            roles
        }, {
            fk_user_id: userId,
            base_id: baseId
        });
        await _NocoCache.default.update(context, `${_globals.CacheScope.BASE_USER}:${baseId}:${userId}`, {
            roles
        });
        (0, _commandPaletteHelpers.cleanCommandPaletteCacheForUser)(userId).catch(()=>{
            logger.error('Error cleaning command palette cache');
        });
        return res;
    }
    static async update(context, baseId, userId, baseUser, ncMeta = _Noco.default.ncMeta) {
        const updateObj = (0, _extractProps.extractProps)(baseUser, [
            'starred',
            'hidden',
            'order'
        ]);
        // set meta
        await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.PROJECT_USERS, updateObj, {
            fk_user_id: userId,
            base_id: baseId
        });
        await _NocoCache.default.update(context, `${_globals.CacheScope.BASE_USER}:${baseId}:${userId}`, updateObj);
        return await this.get(context, baseId, userId, ncMeta);
    }
    static async delete(context, baseId, userId, ncMeta = _Noco.default.ncMeta) {
        // delete meta
        const response = await ncMeta.metaDelete(context.workspace_id, context.base_id, _globals.MetaTable.PROJECT_USERS, {
            fk_user_id: userId,
            base_id: baseId
        });
        // delete list cache to refresh list
        await _NocoCache.default.deepDel(context, `${_globals.CacheScope.BASE_USER}:${baseId}:list`, _globals.CacheDelDirection.PARENT_TO_CHILD);
        (0, _commandPaletteHelpers.cleanCommandPaletteCacheForUser)(userId).catch(()=>{
            logger.error('Error cleaning command palette cache');
        });
        await _index.MCPToken.bulkDelete({
            fk_user_id: userId
        }, ncMeta);
        return response;
    }
    static async getProjectsIdList(userId, ncMeta = _Noco.default.ncMeta) {
        if (!userId) _catchError.NcError.badRequest('User Id is required');
        return await ncMeta.knex(_globals.MetaTable.PROJECT_USERS).where({
            fk_user_id: userId
        });
    }
    static async getProjectsList(userId, params, ncMeta = _Noco.default.ncMeta) {
        // TODO implement CacheScope.USER_BASE
        const qb = ncMeta.knex(_globals.MetaTable.PROJECT).select(`${_globals.MetaTable.PROJECT}.*`).select(`${_globals.MetaTable.PROJECT_USERS}.starred`).select(`${_globals.MetaTable.PROJECT_USERS}.roles as project_role`).select(`${_globals.MetaTable.PROJECT_USERS}.updated_at as last_accessed`).leftJoin(_globals.MetaTable.PROJECT_USERS, function() {
            this.on(`${_globals.MetaTable.PROJECT_USERS}.base_id`, `${_globals.MetaTable.PROJECT}.id`);
            this.andOn(`${_globals.MetaTable.PROJECT_USERS}.fk_user_id`, ncMeta.knex.raw('?', [
                userId
            ]));
        }).where(`${_globals.MetaTable.PROJECT_USERS}.fk_user_id`, ncMeta.knex.raw('?', [
            userId
        ])).where(function() {
            this.where(`${_globals.MetaTable.PROJECT}.deleted`, false).orWhereNull(`${_globals.MetaTable.PROJECT}.deleted`);
        }).where(function() {
            this.whereNull(`${_globals.MetaTable.PROJECT_USERS}.roles`).orWhereNot(`${_globals.MetaTable.PROJECT_USERS}.roles`, _nocodbsdk.ProjectRoles.NO_ACCESS);
        });
        // filter starred bases
        if (params.starred) {
            qb.where(`${_globals.MetaTable.PROJECT_USERS}.starred`, true);
        }
        // filter shared with me bases
        if (params.shared) {
            qb.where(function() {
                // include bases belongs project_user in which user is not owner
                this.where(function() {
                    this.where(`${_globals.MetaTable.PROJECT_USERS}.fk_user_id`, userId).whereNot(`${_globals.MetaTable.PROJECT_USERS}.roles`, _nocodbsdk.ProjectRoles.OWNER).whereNotNull(`${_globals.MetaTable.PROJECT_USERS}.roles`);
                });
            });
        }
        // order based on recently accessed
        if (params.recent) {
            qb.orderBy(`${_globals.MetaTable.PROJECT_USERS}.updated_at`, 'desc');
        }
        qb.whereNot(`${_globals.MetaTable.PROJECT}.deleted`, true);
        const baseList = await qb;
        if (baseList && (baseList == null ? void 0 : baseList.length)) {
            const promises = [];
            const castedProjectList = baseList.filter((p)=>!(params == null ? void 0 : params.type) || p.type === params.type).sort((a, b)=>(a.order != null ? a.order : Infinity) - (b.order != null ? b.order : Infinity)).map((p)=>{
                const base = _Base.default.castType(p);
                base.meta = (0, _modelUtils.parseMetaProp)(base);
                promises.push(base.getSources(false, ncMeta));
                return base;
            });
            await Promise.all(promises);
            return castedProjectList;
        } else {
            return [];
        }
    }
    static async updateOrInsert(context, baseId, userId, baseUser, ncMeta = _Noco.default.ncMeta) {
        const existingProjectUser = await this.get(context, baseId, userId, ncMeta);
        if (existingProjectUser) {
            return await this.update(context, baseId, userId, baseUser, ncMeta);
        } else {
            return await this.insert(context, {
                base_id: baseId,
                fk_user_id: userId,
                invited_by: baseUser.invited_by,
                starred: baseUser.starred,
                order: baseUser.order,
                hidden: baseUser.hidden
            });
        }
    }
    constructor(data){
        Object.assign(this, data);
    }
};
